import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import {
  MembershipCancelReason,
  SitterMembershipCancelReasonsService,
  SitterMembershipCancelService,
  SitterPlan,
  SitterPlanService,
} from 'us-web-services';
import { getLongDateString } from 'us-react-components/dist/utils/date';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const OTHER_REASON_ID = 7;

export const CancelReasons = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [activePlan, setActivePlan] = useState<SitterPlan | null>(null);
  const [reasons, setReasons] = useState<MembershipCancelReason[]>([]);
  const [selected, setSelected] = useState<MembershipCancelReason | null>(null);
  const [reasonText, setReasonText] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Cancel Membership';
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const loadPlan = async (): Promise<void> => {
      try {
        const resp = await SitterPlanService.getByFilter({
          params: { active: true, userId: user.id },
        });
        if (cancelled) return;
        const list = (resp.data?.data ?? []) as SitterPlan[];
        if (list.length) {
          setActivePlan(list[0]);
        } else {
          navigate('/settings', { replace: true });
        }
      } catch (err) {
        console.warn('[CaregiverCancelReasons] plan load failed', err);
      }
    };

    const loadReasons = async (): Promise<void> => {
      try {
        const resp = await SitterMembershipCancelReasonsService.getByFilter({
          params: { id: user.id, active: true },
        });
        if (cancelled) return;
        const list = resp.data?.data ?? [];
        setReasons(list);
      } catch (err) {
        console.warn('[CaregiverCancelReasons] reasons load failed', err);
      }
    };

    void (async (): Promise<void> => {
      await Promise.allSettled([loadPlan(), loadReasons()]);
      if (!cancelled) setLoading(false);
    })();

    return (): void => {
      cancelled = true;
    };
  }, [user?.id, navigate]);

  const textRequired = selected?.id === OTHER_REASON_ID;
  const canSubmit =
    !!selected && !submitting && (!textRequired || reasonText.trim().length > 0);

  const planEndDate = getLongDateString(activePlan?.end);

  const goNext = (): void => {
    if (user?.id) {
      navigate(`/sitter/${user.id}/settings`);
    } else {
      navigate('/');
    }
  };

  const ensurePlanCancelled = async (): Promise<void> => {
    if (!activePlan?.id || activePlan.cancelAt || !user?.id) return;
    await SitterPlanService.patch(activePlan.id, {
      id: activePlan.id,
      userId: activePlan.userId ?? user.id,
      cancel: true,
    });
  };

  const skip = async (): Promise<void> => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await ensurePlanCancelled();
      goNext();
    } catch {
      toast.error(t('pages.cancelReasons.submitFailed'));
      setSubmitting(false);
    }
  };

  const submit = async (): Promise<void> => {
    if (!canSubmit || !user?.id || !activePlan?.id || !selected) return;
    setSubmitting(true);
    try {
      await ensurePlanCancelled();
      await SitterMembershipCancelService.create({
        sitterId: user.id,
        reason: selected,
        cancelText: reasonText,
        userPlanId: activePlan.id,
      });
      goNext();
    } catch {
      toast.error(t('pages.cancelReasons.submitFailed'));
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className='container py-8 text-center'>
        <p className='text-neutral-600'>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className='container py-8 max-w-2xl mx-auto'>
      <h1 className='text-2xl font-semibold mb-2'>{t('pages.cancelReasons.heading')}</h1>
      <p className='mb-6'>
        {t('pages.cancelReasons.retainAccessPrefix')} <b>{planEndDate}</b>
        {t('pages.cancelReasons.retainAccessSuffix')}
      </p>

      <h3 className='text-lg font-semibold mb-1'>{t('pages.cancelReasons.pleaseTellUs')}</h3>
      <p className='mb-4 text-sm'>{t('pages.cancelReasons.chooseOrWrite')}</p>

      <div className='space-y-2 mb-4'>
        {reasons.map((reason) => {
          const isSelected = selected?.id === reason.id;
          return (
            <Button
              key={reason.id}
              id={`cancel-reason-${reason.id}`}
              buttonType={ButtonType.tertiary}
              onClick={(): void => setSelected(reason)}
              className={`block w-full text-left rounded border px-4 py-3 transition ${
                isSelected
                  ? 'border-sapphire bg-sapphire-50'
                  : 'border-neutral-200 hover:bg-neutral-50'
              }`}
            >
              <span className='font-medium text-sapphire'>{reason.label}</span>
            </Button>
          );
        })}
      </div>

      <textarea
        rows={4}
        value={reasonText}
        onChange={(e): void => setReasonText(e.target.value)}
        disabled={!selected}
        placeholder={
          textRequired
            ? t('pages.cancelReasons.textareaRequired')
            : t('pages.cancelReasons.textareaOptional')
        }
        className='w-full border border-neutral-300 rounded p-2 mb-6 focus:ring-sapphire focus:border-sapphire disabled:bg-neutral-100'
      />

      <div className='flex gap-3'>
        <Button
          id='skip-reason'
          buttonType={ButtonType.secondary}
          onClick={skip}
          disabled={submitting}
        >
          {t('pages.cancelReasons.skip')}
        </Button>
        <Button
          id='submit-cancel-reason'
          buttonType={ButtonType.primary}
          onClick={submit}
          disabled={!canSubmit}
        >
          {t('pages.cancelReasons.submit')}
        </Button>
      </div>
    </div>
  );
});
